import React, { useState } from "react";
import { Share2, Copy, Check, QrCode } from "lucide-react";
import toast from "react-hot-toast";
import StyledShareQr from "./StyledShareQr";

/**
 * Public share-link card shown on the agent's property detail page.
 *
 * Renders the property's social-media ad URL — preferring the new short, address-aware
 * format (https://portal.collings.com.au/p/12-fake-st-camberwell-k7m2p9x3) and falling
 * back to the legacy UUID form for properties that haven't been resaved since the
 * `share_short_id` migration. Visitors hitting either URL see a public property
 * preview but must register on the buyer portal to interact.
 *
 * Backend: `share_slug` is computed server-side from address fields + `share_short_id`,
 * and is exposed on the property-management detail endpoint alongside the legacy
 * `share_token` UUID.
 */
const ShareLinkCard = ({ property }) => {
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  // Prefer the new short slug; fall back to the UUID token so the card still renders
  // for any property loaded before the backend migration is applied.
  const slug = property?.share_slug || property?.share_token;
  if (!slug) return null;

  const buyerPortalBase =
    process.env.REACT_APP_BUYER_PORTAL_URL || "https://portal.collings.com.au";
  const shareUrl = `${buyerPortalBase}/p/${slug}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  // Identifiable filename for downloaded codes, derived from the slug.
  const safeSlug = String(slug).replace(/[^a-z0-9-]/gi, "-").slice(0, 60) || "property";

  const clickCount = property?.portal_click_count;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
      <h3 className="text-[13px] font-bold text-[#111827] mb-1 flex items-center gap-2">
        <Share2 className="w-4 h-4 text-[#55C5D0]" />
        Public Share Link
      </h3>
      <p className="text-[11px] text-gray-500 mb-3 leading-relaxed">
        Use this link in social media ads. Visitors can preview the listing without an
        account, but must register to enquire, save, or make an offer.
      </p>
      <div className="flex items-stretch gap-2">
        <input
          readOnly
          value={shareUrl}
          onFocus={(e) => e.target.select()}
          className="flex-1 min-w-0 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-[12px] text-gray-700 font-mono focus:outline-none focus:border-[#55C5D0]"
        />
        <button
          type="button"
          onClick={handleCopy}
          className={`px-3 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 ${copied
            ? "bg-green-50 text-green-700 border border-green-200"
            : "bg-[#55C5D0] hover:bg-[#4AADB7] text-white shadow-sm"
            }`}
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
        <button
          type="button"
          onClick={() => setShowQr((v) => !v)}
          aria-expanded={showQr}
          className={`px-3 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 border ${showQr
            ? "bg-[#55C5D0] text-white border-[#55C5D0] shadow-sm"
            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
            }`}
        >
          <QrCode className="w-3.5 h-3.5" />
          QR
        </button>
      </div>

      {showQr && (
        <div className="mt-3 flex flex-col items-center bg-gray-50 rounded-lg border border-gray-200 p-4">
          <StyledShareQr url={shareUrl} filename={safeSlug} />
        </div>
      )}
      {typeof clickCount === "number" && (
        <p className="text-[11px] text-gray-500 mt-3">
          <span className="font-bold text-[#111827]">{clickCount}</span> click
          {clickCount === 1 ? "" : "s"} from share links
        </p>
      )}
    </div>
  );
};

export default ShareLinkCard;
